import React from "react";
import { getConfig } from "../../config/configuration";
import { Player, BackhandType, PlayStyle } from "../../store/players/models";
import { Paper, Typography, CircularProgress, Link } from "@material-ui/core";
import { Data } from "../../framework/data";
import { useAuth0 } from "@auth0/auth0-react";
import { Clubs } from "../../store/leagues/models";

interface LeaguePlayerViewProperties {
  playerId: string;
  leagueId: string;
  leagueYear: string;
  clubs: Data<Clubs[]>;
}

const backHandToString = (backhand: BackhandType) => {
  switch (backhand) {
    case BackhandType.OneHanded:
      return "Jednoręczny";
    case BackhandType.TwoHanded:
      return "Dwuręczny";
    default:
      return "";
  }
}

const playStyleToString = (style: PlayStyle) => {
  switch (style) {
    case PlayStyle.Offensive:
      return "Ofensywny";
    case PlayStyle.Defensive:
      return "Defensywny";
    case PlayStyle.Neutral:
      return "Neutralny";
    default:
      return "";
  }
};

const LeaguePlayerView: React.FC<LeaguePlayerViewProperties> = ({
  playerId,
  leagueId,
  leagueYear,
  clubs,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const [player, setPlayer] = React.useState<Player | undefined>();
  const [tenant, setTenant] = React.useState<string>();

  React.useEffect(() => {
    getConfig()
      .then((c) => {
        const url = `/leagues/open/${leagueYear}/${leagueId}/players/${playerId}`;
        setTenant(c.key);
        return `${c.apiUrl}${url}`;
      })
      .then((url) => {
        return getAccessTokenSilently().then((token: string) => ({
          token,
          url,
        }));
      })
      .then(({ token, url }) => {
        return fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          method: "GET",
        });
      })
      .then((resp) => (resp.ok ? resp.json() : Promise.reject()))
      .then(setPlayer);
  }, [playerId]);

  return (
    <Paper style={{ padding: "16px", minWidth: "250px" }} elevation={0}>
      {!!player ? (
        <>
          <Typography variant="body2">
            Imię i nazwisko:{" "}
            <Link href={`/#/stats/${btoa(player?.id || "")}`}>
              <span style={{ fontWeight: "bold" }}>
                {player.name} {player.surname}
              </span>
            </Link>
          </Typography>
          <Typography variant="body2">
            Telefon:{" "}
            <span style={{ fontWeight: "bold" }}>{player.phoneNumber}</span>
          </Typography>
          <Typography variant="body2">
            Adres email:{" "}
            <span style={{ fontWeight: "bold" }}>{player.email}</span>
          </Typography>
          <Typography variant="body2">
            Poziom NTRP:{" "}
            <span style={{ fontWeight: "bold" }}>{player.level}</span>
          </Typography>
          
          {clubs.type === "LOADED" && !!player.preferredClubs && player.preferredClubs.length > 0 ? (<>
            <Typography variant="body2">Preferowane kluby: </Typography>
            <ul style={{ margin: 0 }}>
              {clubs.value
                .filter(
                  (c) =>
                    !!player.preferredClubs &&
                    player.preferredClubs.includes(c.id)
                )
                .map((c) => (
                  <li key={c.id} style={{ fontWeight: "bold" }} className="MuiTypography-body2">
                    {c.name}
                  </li>
                ))}
            </ul>
          </>) : (
              <></>
            )}

          {
            !!player.preferredTime && (<><Typography variant="body2">Preferowane godziny: </Typography>
              <Typography variant="body2">
                <span style={{ fontWeight: "bold" }}>
                  {player.preferredTime || ""}
                </span>
              </Typography></>)
          }

          {
            !!player.heightCm && tenant === "gorzow" && (<Typography variant="body2">
              Wzrost (cm):{" "}
              <span style={{ fontWeight: "bold" }}>{player.heightCm}</span>
            </Typography>)
          }
          {
            player.backhand !== null && player.backhand !== undefined  && tenant === "gorzow" &&(<Typography variant="body2">
              Bekhend:{" "}
              <span style={{ fontWeight: "bold" }}>{backHandToString(player.backhand)}</span>
            </Typography>)
          }
          {
            player.style !== null && player.style !== undefined && tenant === "gorzow" &&(<Typography variant="body2">
              Styl gry:{" "}
              <span style={{ fontWeight: "bold" }}>{playStyleToString(player.style)}</span>
            </Typography>)
          }
          {
            !!player.homeClub && tenant === "gorzow" && (<Typography variant="body2">
              Macierzysty kort:{" "}
              <span style={{ fontWeight: "bold" }}>{player.homeClub}</span>
            </Typography>)
          }
        </>
      ) : (
          <CircularProgress />
        )}
    </Paper>
  );
};

export default LeaguePlayerView;
